import React from 'react';
import PropTypes from 'prop-types';

import { ALBUM_SUBTYPE } from '../../../../../../../constants/albums';
import { getAsset } from '../../../../../../../util/assets';
import {
  includesEventItemByType,
  includesWaldofiedAddonItem,
} from '../../util';
import { EVENT_ADDON_ITEM_TYPE } from '../../../../../../../constants/event-items';
import FreemiumEventLandingPageFeatureList from './components/FreemiumEventLandingPageFeatureList';
import { getEventAddonItemsByType } from '../../../../../components/FreemiumEventPurchase/helpers';
import Badge from '../../../../../../../components/global/Badge';
import { formatPriceForDisplay } from '../../../../../../../util/format-money';
import Button from '../../../../../../../components/global/Button';

const propTypes = {
  stepProps: PropTypes.shape({
    eventType: PropTypes.string,
  }),
};

const FreemiumEventLandingPageStep = ({ stepProps }) => {
  const {
    eventType,
    eventAddonItems,
    requiresSKUSelection,
    currentSelectedEventAddonItemIds,
    includePhotosAlbum,
    eventAddonItemTypesUpsell,
  } = stepProps;

  const data = (() => {
    // Upsell only case:
    if (
      !requiresSKUSelection &&
      includePhotosAlbum &&
      !eventAddonItemTypesUpsell.includes(EVENT_ADDON_ITEM_TYPE.WALDOFIED) &&
      !includesWaldofiedAddonItem(
        eventAddonItems,
        currentSelectedEventAddonItemIds,
      )
    ) {
      const features = [
        'Auto-generated QR Code signage',
        'AI-powered guests photo delivery',
        'Live Slideshow',
      ];

      switch (eventType) {
        case ALBUM_SUBTYPE.WEDDINGS:
          return {
            subtitle:
              "Let's Waldo-fy your event with the simplest & easiest wedding guest photo app",
            imageUrl: getAsset('/freemium-event-config/wedding-free-lp.png'),
            content: (
              <FreemiumEventLandingPageFeatureList features={features} />
            ),
          };

        default: {
          return {
            subtitle:
              "Let's Waldo-fy your event with the simplest & easier photo sharing app",
            imageUrl: getAsset('/freemium-event-config/lp.png'),
            content: (
              <FreemiumEventLandingPageFeatureList features={features} />
            ),
          };
        }
      }
    }

    if (
      !requiresSKUSelection &&
      includesWaldofiedAddonItem(
        eventAddonItems,
        currentSelectedEventAddonItemIds,
      )
    ) {
      const [waldofiedItem] = getEventAddonItemsByType(
        eventAddonItems,
        EVENT_ADDON_ITEM_TYPE.WALDOFIED,
      );

      const features = [
        'Auto-generated QR Code signage',
        'AI-powered guests photo delivery',
        'Live Slideshow',
      ];

      switch (eventType) {
        case ALBUM_SUBTYPE.WEDDINGS:
          return {
            decorationUrl: getAsset(
              '/freemium-event-config/wedding-config-decoration.png',
            ),
            price: waldofiedItem?.price?.value,
            subtitle:
              "Let's Waldo-fy your event with the simplest & easiest wedding guest photo app",
            imageUrl: getAsset(
              '/freemium-event-config/wedding-waldofied-lp.png',
            ),
            content: (
              <FreemiumEventLandingPageFeatureList features={features} />
            ),
          };

        default: {
          return {
            decorationUrl: getAsset(
              eventType === ALBUM_SUBTYPE.MEMORIALS
                ? '/freemium-event-config/wedding-config-decoration.png'
                : '/freemium-event-config/generic-config-decoration.png',
            ),
            subtitle:
              "Let's Waldo-fy your event with the simplest & easier photo sharing app",
            price: waldofiedItem?.price?.value,
            imageUrl: getAsset('/freemium-event-config/waldofied-lp.png'),
            content: (
              <FreemiumEventLandingPageFeatureList features={features} />
            ),
          };
        }
      }
    }

    if (
      !requiresSKUSelection &&
      includesEventItemByType(
        eventAddonItems,
        currentSelectedEventAddonItemIds,
        EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO,
      )
    ) {
      const [groupVideoItem] = getEventAddonItemsByType(
        eventAddonItems,
        EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO,
      );

      switch (eventType) {
        case ALBUM_SUBTYPE.WEDDINGS:
          return {
            decorationUrl: getAsset(
              '/freemium-event-config/wedding-config-decoration.png',
            ),
            price: groupVideoItem?.price?.value,
            subtitle: 'Easy QR code upload with an AI-concierge',
            imageUrl: getAsset(
              '/freemium-event-config/wedding-video-guestbook-lp.png',
            ),
          };
        case ALBUM_SUBTYPE.MEMORIALS:
          return {
            decorationUrl: getAsset(
              '/freemium-event-config/wedding-config-decoration.png',
            ),
            price: groupVideoItem?.price?.value,
            subtitle: 'Easy QR code upload with an AI-concierge',
            imageUrl: getAsset(
              '/freemium-event-config/memorial-group-video-lp.png',
            ),
          };

        default: {
          return {
            decorationUrl: getAsset(
              '/freemium-event-config/generic-config-decoration.png',
            ),
            price: groupVideoItem?.price?.value,
            subtitle:
              'AI-concierge to collect video messages from others and turn them into a Group Video. No editing skills required.',
            imageUrl: getAsset('/freemium-event-config/group-video-lp.png'),
          };
        }
      }
    }

    if (
      !requiresSKUSelection &&
      includesEventItemByType(
        eventAddonItems,
        currentSelectedEventAddonItemIds,
        EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK,
      )
    ) {
      const [memoryBookItem] = getEventAddonItemsByType(
        eventAddonItems,
        EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK,
      );

      const features = [
        'AI-concierge to gather old photos from others',
        'Auto-generated photo slideshow',
        'No editing skills required',
      ];

      switch (eventType) {
        case ALBUM_SUBTYPE.WEDDINGS: {
          return {
            decorationUrl: getAsset(
              '/freemium-event-config/wedding-config-decoration.png',
            ),
            price: memoryBookItem?.price?.value,
            imageUrl: getAsset(
              '/freemium-event-config/wedding-memory-book-lp.png',
            ),
            content: (
              <FreemiumEventLandingPageFeatureList features={features} />
            ),
          };
        }
        case ALBUM_SUBTYPE.MEMORIALS: {
          return {
            decorationUrl: getAsset(
              '/freemium-event-config/wedding-config-decoration.png',
            ),
            price: memoryBookItem?.price?.value,
            imageUrl: getAsset(
              '/freemium-event-config/memorial-memory-book-lp.png',
            ),
            content: (
              <FreemiumEventLandingPageFeatureList features={features} />
            ),
          };
        }
        default: {
          return {
            decorationUrl: getAsset(
              '/freemium-event-config/generic-config-decoration.png',
            ),
            price: memoryBookItem?.price?.value,
            imageUrl: getAsset('/freemium-event-config/memory-book-lp.png'),
            content: (
              <FreemiumEventLandingPageFeatureList features={features} />
            ),
          };
        }
      }
    }

    const features = [
      'Auto-generated QR Code signage',
      'AI-powered guests photo delivery',
      'Live Slideshow',
    ];

    switch (eventType) {
      case ALBUM_SUBTYPE.WEDDINGS: {
        return {
          decorationUrl: getAsset(
            '/freemium-event-config/wedding-config-decoration.png',
          ),
          subtitle:
            "Let's Waldo-fy your event with the simplest & easiest wedding photo app.",
          imageUrl: getAsset('/freemium-event-config/wedding-lp.png'),
          content: <FreemiumEventLandingPageFeatureList features={features} />,
        };
      }

      default: {
        return {
          decorationUrl: getAsset(
            eventType === ALBUM_SUBTYPE.MEMORIALS
              ? '/freemium-event-config/wedding-config-decoration.png'
              : '/freemium-event-config/generic-config-decoration.png',
          ),
          subtitle:
            "Let's Waldo-fy your event with the simplest & easier photo sharing app",
          imageUrl: getAsset('/freemium-event-config/lp.png'),
          content: <FreemiumEventLandingPageFeatureList features={features} />,
        };
      }
    }
  })();

  return (
    <>
      {data.decorationUrl && (
        <>
          <img
            src={data.decorationUrl}
            alt="decoration top left"
            className="freemium-event-wizard-landing-decoration freemium-event-wizard-landing-decoration-tl"
          />
          <img
            src={data.decorationUrl}
            alt="decoration bottom right"
            className="freemium-event-wizard-landing-decoration freemium-event-wizard-landing-decoration-br"
          />
        </>
      )}
      <div className="freemium-event-wizard-landing-page-wrapper">
        <img
          src={data.imageUrl}
          alt="landing page"
          className="freemium-event-wizard-landing-page-image"
        />
        <div className="freemium-event-wizard-landing-page-content">
          <h2 className="text-center text-md-left">Welcome to Waldo</h2>
          {data.subtitle && (
            <p className="text-bold text-dark text-center text-md-left">
              {data.subtitle}
            </p>
          )}

          {data.price && (
            <div className="d-flex align-items-center justify-content-center mb-1">
              <Badge
                badgeStyle="blue-mid-light"
                badgeSize="lg"
                className="text-primary text-extra-bold"
              >
                ${formatPriceForDisplay(data.price)}
              </Badge>
            </div>
          )}
          {data.content}
          <Button
            type="submit"
            className="freemium-create-wizard-lp-btn"
            data-testid="album-create-wizard-create-btn"
          >
            Start setting up my event
          </Button>
        </div>
      </div>
    </>
  );
};

FreemiumEventLandingPageStep.propTypes = propTypes;

export default FreemiumEventLandingPageStep;
